import React, { useState, useMemo, useEffect } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, enrichClientRow, clientDisplayAssignee, clientMatchesAssignee } from '../../core/helpers';
import { Badge } from '../../components/Badge';
import { ClientDetail } from './ClientDetail';
import { Icon, InlineIcon } from '../../components/Icon';
import { buildApiFilter, usePaginatedApiRows } from '../../core/searchFilters';
import { LoadMoreBar } from '../../components/LoadMoreBar';
import { buildOrderParam } from '../../core/tableView';
import { SortableTh } from '../../components/SortableTh';

const CLIENT_ORDER = { name: 'name', billing: 'total_billing', collected: 'collected', pending: 'pending', createdAt: 'created_at' };

const ALL_CLIENT_COLUMNS = [
  { id: 'type', label: 'Type' },
  { id: 'contact', label: 'Contact' },
  { id: 'assignedTo', label: 'Assigned To' },
  { id: 'billing', label: 'Billing' },
  { id: 'collected', label: 'Collected' },
  { id: 'pending', label: 'Pending' },
  { id: 'progress', label: 'Progress' },
  { id: 'tasks', label: 'Tasks' },
];

const DEFAULT_COLUMN_IDS = ALL_CLIENT_COLUMNS.map(c => c.id);

function loadColumnPrefs(userId) {
  try {
    const raw = localStorage.getItem(`fb_client_columns_${userId}`);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(parsed)) return DEFAULT_COLUMN_IDS;
    const allowed = new Set(DEFAULT_COLUMN_IDS);
    const clean = parsed.filter(id => allowed.has(id));
    return clean.length ? clean : DEFAULT_COLUMN_IDS;
  } catch {
    return DEFAULT_COLUMN_IDS;
  }
}

function matchProgressFilter(pct, filter) {
  if (!filter || filter === 'all') return true;
  const p = Number(pct) || 0;
  if (filter === '0') return p === 0;
  if (filter === '1-49') return p >= 1 && p <= 49;
  if (filter === '50-99') return p >= 50 && p <= 99;
  if (filter === '100') return p === 100;
  return true;
}

function ClientsPage() {
  const { clients, employees, tasks, openModal, user, can, invoices, payments, notifFocus, setNotifFocus, showToast } = useApp();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [progressFilter, setProgressFilter] = useState("all");
  const [selectedClient, setSelectedClient] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [submittingDelete, setSubmittingDelete] = useState(false);

  const submitDeleteRequest = async () => {
    if (!confirmDelete) return;
    setSubmittingDelete(true);
    try {
      await sb.createDeleteRequest({
        entity_type: "clients",
        entity_id: confirmDelete.id,
        reason: deleteReason.trim(),
        company_id: confirmDelete.id,
        reassign_to: null,
      });
      showToast("Delete request submitted — awaiting admin approval", "success");
      setConfirmDelete(null);
      setDeleteReason("");
    } catch (err) {
      showToast(err?.message || "Failed to submit delete request", "error");
    } finally {
      setSubmittingDelete(false);
    }
  };
  useEffect(() => {
    if (!notifFocus?.clientId) return;
    const c = clients.find(x => x.id === notifFocus.clientId);
    if (c) setSelectedClient(c);
    setNotifFocus(null);
  }, [notifFocus?.clientId, clients, setNotifFocus]);
  const [activeColumns, setActiveColumns] = useState(() => loadColumnPrefs(user?.id || 'default'));
  const [showColumns, setShowColumns] = useState(false);
  const [sortKey, setSortKey] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const showCol = (id) => activeColumns.includes(id);

  const canEdit = (c) => {
    if (user.role === "admin") return true;
    if (user.role === "manager") return c.assignedTo === user.id;
    if (user.role === "employee") return c.assignedTo === user.id;
    return false;
  };

  const localVisible = clients.filter(c => {
    if (user.role === "employee" && c.assignedTo !== user.id) return false;
    if (filter !== "all" && c.status !== filter) return false;
    if (assigneeId && !clientMatchesAssignee(c, assigneeId, tasks, clients)) return false;
    if (search) {
      const q = search.toLowerCase();
      const hitName = (c.name || '').toLowerCase().includes(q);
      const hitNo = String(c.clientNo || c.client_no || '').toLowerCase().includes(q);
      if (!hitName && !hitNo) return false;
    }
    if (from && c.createdAt && new Date(c.createdAt) < new Date(from)) return false;
    if (to && c.createdAt && new Date(c.createdAt) > new Date(to + 'T23:59:59')) return false;
    return true;
  });

  const apiFilter = buildApiFilter({
    equals: {
      ...(user.role === "employee" ? { assigned_to: user.id } : {}),
      ...(filter !== "all" ? { status: filter } : {}),
    },
    from, to,
    dateField: "created_at",
    search,
    searchFields: ["name", "client_no"],
    order: buildOrderParam(sortKey, sortDir, CLIENT_ORDER) || "created_at.desc",
  });

  const { rows: visibleRaw, hasMore, loadMore, loadingMore } = usePaginatedApiRows(
    "clients", apiFilter, localVisible,
    { enabled: !!apiFilter, rollupInvoices: invoices, rollupTasks: tasks, rollupPayments: payments },
  );

  const visible = useMemo(() => {
    return visibleRaw
      .map(c => enrichClientRow(c, invoices, tasks, payments))
      .filter(c => matchProgressFilter(c.progress, progressFilter))
      .filter(c => !assigneeId || clientMatchesAssignee(c, assigneeId, tasks, clients));
  }, [visibleRaw, invoices, tasks, payments, progressFilter, assigneeId, clients]);

  const toggleColumn = (id) => {
    setActiveColumns(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      const clean = next.length ? next : DEFAULT_COLUMN_IDS;
      if (user?.id) localStorage.setItem(`fb_client_columns_${user.id}`, JSON.stringify(clean));
      return clean;
    });
  };

  const clearFilters = () => {
    setSearch("");
    setFilter("all");
    setFrom("");
    setTo("");
    setAssigneeId("");
    setProgressFilter("all");
  };

  if (selectedClient) {
    return <ClientDetail client={selectedClient} onBack={() => setSelectedClient(null)} canEdit={canEdit(selectedClient)} />;
  }

  return (
    <>
      <div className="list-toolbar">
        <div className="list-toolbar-search">
          <input className="f-input" placeholder="Search client name or ID..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="chips list-toolbar-chips">
          {["all", "active", "pending"].map(f => (
            <div key={f} className={`chip ${filter === f ? "on" : ""}`} onClick={() => setFilter(f)} style={{ textTransform: "capitalize" }}>{f}</div>
          ))}
        </div>
        <div className="list-toolbar-dates">
          <label className="list-toolbar-date">
            <span className="list-toolbar-date-lbl">From</span>
            <input className="f-input" type="date" value={from} onChange={e => setFrom(e.target.value)} />
          </label>
          <label className="list-toolbar-date">
            <span className="list-toolbar-date-lbl">To</span>
            <input className="f-input" type="date" value={to} onChange={e => setTo(e.target.value)} />
          </label>
        </div>
        {isAdmin && (
          <select className="f-select" style={{ minWidth: 160 }} value={assigneeId} onChange={e => setAssigneeId(e.target.value)}>
            <option value="">All assignees</option>
            {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
          </select>
        )}
        <select className="f-select" style={{ minWidth: 140 }} value={progressFilter} onChange={e => setProgressFilter(e.target.value)}>
          <option value="all">All progress</option>
          <option value="0">0% — not started</option>
          <option value="1-49">1–49%</option>
          <option value="50-99">50–99%</option>
          <option value="100">100% complete</option>
        </select>
        <div className="list-toolbar-actions">
          {(from || to || assigneeId || progressFilter !== 'all' || filter !== 'all' || search) && (
            <button type="button" className="btn btn-sm" onClick={clearFilters}>
              <InlineIcon name="close" size={13} gap={4}>Clear filters</InlineIcon>
            </button>
          )}
          {isAdmin && (
            <button type="button" className="btn btn-sm" onClick={() => setShowColumns(s => !s)}>
              <InlineIcon name="settings" size={13} gap={4}>Columns</InlineIcon>
            </button>
          )}
          {can("clients", "create") && (
            <button type="button" className="btn btn-primary" onClick={() => openModal("create-client")}>+ New Client</button>
          )}
        </div>
      </div>

      {isAdmin && showColumns && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12, alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Show columns:</span>
          {ALL_CLIENT_COLUMNS.map(col => (
            <label key={col.id} className="f-check" style={{ fontSize: 12 }}>
              <input type="checkbox" checked={showCol(col.id)} onChange={() => toggleColumn(col.id)} />
              {col.label}
            </label>
          ))}
        </div>
      )}

      <div className="card">
        <div className="tbl-freeze-col table-scroll-shell">
          <div className="tbl-wrap tbl-clients">
            <table>
            <thead>
              <tr>
                <SortableTh label="Client" sortKey="name" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                {showCol('type') && <th>Type</th>}
                {showCol('contact') && <th>Contact</th>}
                {showCol('assignedTo') && <th>Assigned To</th>}
                {showCol('billing') && <SortableTh label="Billing" sortKey="billing" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />}
                {showCol('collected') && <SortableTh label="Collected" sortKey="collected" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />}
                {showCol('pending') && <SortableTh label="Pending" sortKey="pending" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />}
                {showCol('progress') && <th>Progress</th>}
                {showCol('tasks') && <th>Tasks</th>}
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(c => {
                const clientTasks = tasks.filter(t => t.clientId === c.id);
                const emp = employees.find(e => e.id === clientDisplayAssignee(c, clientTasks));
                const done = clientTasks.filter(t => t.status === "completed").length;
                return (
                  <tr key={c.id} style={{ cursor: "pointer" }} onClick={() => setSelectedClient(c)}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: "var(--muted)" }}>{c.clientNo} · {c.createdAt}</div>
                    </td>
                    {showCol('type') && <td><span className="tag">{c.type}</span></td>}
                    {showCol('contact') && (
                      <td>
                        <div style={{ fontSize: 12 }}>{c.contactName}</div>
                        <div style={{ fontSize: 11, color: "var(--muted)" }}>{c.phone}</div>
                      </td>
                    )}
                    {showCol('assignedTo') && (
                      <td>{emp
                        ? <div style={{ display:"flex",alignItems:"center",gap:6 }}><div className="av av-sm" style={{ background:emp.color }}>{emp.avatar}</div><span style={{ fontSize:12 }}>{emp.name}</span></div>
                        : clientDisplayAssignee(c, clientTasks)
                          ? <span style={{ fontSize:11,color:"var(--muted)" }}>Loading…</span>
                          : <span style={{ fontSize:11,color:"var(--muted)" }}>Unassigned</span>
                      }</td>
                    )}
                    {showCol('billing') && <td style={{ fontWeight: 700 }}>{INR(c.totalBilling)}</td>}
                    {showCol('collected') && <td style={{ color: "var(--green)", fontWeight: 600 }}>{INR(c.collected)}</td>}
                    {showCol('pending') && <td style={{ color: c.pending > 0 ? "var(--red)" : "var(--muted)", fontWeight: 600 }}>{INR(c.pending)}</td>}
                    {showCol('progress') && (
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 100 }}>
                          <div className="prog-bg" style={{ flex: 1 }}><div className="prog-fill" style={{ width: c.progress + "%", background: c.progress === 100 ? "var(--green)" : "var(--blue)" }} /></div>
                          <span style={{ fontSize: 12, fontWeight: 700 }}>{c.progress}%</span>
                        </div>
                      </td>
                    )}
                    {showCol('tasks') && <td style={{ fontSize: 12 }}>{done}/{clientTasks.length}</td>}
                    <td style={{whiteSpace:"nowrap"}} onClick={e => e.stopPropagation()}>
                      <button className="btn btn-sm" onClick={() => setSelectedClient(c)}>View →</button>{' '}
                      <button className="btn btn-sm" title="Reset Password" style={{background:"#FEF2F2",color:"var(--red)",border:"1px solid #FCA5A5"}} onClick={() => openModal("reset-password",{userId:c.id,email:c.email,name:c.contactName||c.name})}><Icon name="lock" size={14} /></button>{' '}
                      {can("clients","delete") && (
                        <button className="btn btn-sm" style={{background:"#FEF2F2",color:"var(--red)",border:"1px solid #FCA5A5"}} onClick={()=>{ setConfirmDelete(c); setDeleteReason(""); }}>
                          <InlineIcon name="trash" size={13} gap={4}>Delete</InlineIcon>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {visible.length === 0 && (
                <tr><td colSpan={20} style={{ textAlign: 'center', padding: 24, color: 'var(--muted)' }}>No clients match these filters</td></tr>
              )}
            </tbody>
            </table>
          </div>
        </div>
        {confirmDelete && (
          <div className="dr-confirm-form" style={{ padding:"16px 20px", background:"#FEF2F2", borderTop:"1px solid #FCA5A5", borderBottom:"1px solid #FCA5A5", margin:"0 -20px 16px" }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#991B1B", marginBottom:6 }}>Delete "{confirmDelete.name}"?</div>
            <div style={{ fontSize:12, color:"#991B1B", marginBottom:4 }}>This requires admin approval before taking effect.</div>
            <div style={{ fontSize:11, color:"#7F1D1D", marginBottom:10 }}>Deleting this client will hide them from lists. Open invoices and tasks will remain.</div>
            <div className="f-group" style={{ marginBottom:10 }}>
              <label className="f-label">Reason for deletion (optional)</label>
              <textarea className="f-textarea" rows={2} value={deleteReason} onChange={e=>setDeleteReason(e.target.value)} placeholder="Add a reason for this deletion request…" />
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button className="btn btn-sm" onClick={()=>{ setConfirmDelete(null); setDeleteReason(""); }} disabled={submittingDelete}>Cancel</button>
              <button className="btn btn-sm btn-red" onClick={submitDeleteRequest} disabled={submittingDelete}>
                <InlineIcon name="trash" size={13} gap={5}>{submittingDelete ? "Submitting…" : "Submit Delete Request"}</InlineIcon>
              </button>
            </div>
          </div>
        )}
        <LoadMoreBar hasMore={hasMore} loadingMore={loadingMore} onLoadMore={loadMore} count={visible.length} />
      </div>
    </>
  );
}

export { ClientsPage };
